import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getSegments, getConversionMetrics } from '../api/client';

// Segment display metadata: label + tailwind pill colors + the conversion lever.
const SEGMENTS: Record<string, { label: string; cls: string; lever: string }> = {
  lapsed_payer:       { label: 'Lapsed payer',       cls: 'bg-fuchsia-100 text-fuchsia-800', lever: 'Re-sell (top)' },
  capped:             { label: 'Hit the cap',        cls: 'bg-red-100 text-red-700',       lever: 'Convert now' },
  near_cap:           { label: 'Near cap',           cls: 'bg-amber-100 text-amber-700',   lever: 'Convert (prime)' },
  lapsed_capped:      { label: 'Repeat capper',      cls: 'bg-orange-100 text-orange-700', lever: 'Upgrade win-back' },
  filtered_no_report: { label: 'Filter, no report',  cls: 'bg-amber-100 text-amber-800',   lever: 'Aha-moment' },
  setup_errored:      { label: 'Setup errored',      cls: 'bg-amber-100 text-amber-800',   lever: 'Rescue' },
  installed_no_setup: { label: 'Installed, no setup',cls: 'bg-gray-100 text-gray-600',     lever: 'Activation' },
  churned:            { label: 'Churned',            cls: 'bg-red-100 text-red-700',       lever: 'Win-back' },
  activated:          { label: 'Activated',          cls: 'bg-sky-100 text-sky-700',       lever: 'Habit' },
  habitual:           { label: 'Habitual',           cls: 'bg-emerald-100 text-emerald-700', lever: 'Expansion' },
  whale_agency:       { label: 'Whale / agency',     cls: 'bg-emerald-100 text-emerald-800', lever: 'Referral' },
  steady_free:        { label: 'Steady free',        cls: 'bg-gray-100 text-gray-500',     lever: 'Low-touch' },
  abandoned:          { label: 'Abandoned / test',   cls: 'bg-gray-100 text-gray-400',     lever: 'Exclude' },
};

function Pill({ segment }: { segment: string }) {
  const m = SEGMENTS[segment] ?? { label: segment, cls: 'bg-gray-100 text-gray-600', lever: '' };
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${m.cls}`}>{m.label}</span>;
}

function MetricCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="text-2xl font-semibold tabular-nums text-gray-900">{value}</div>
      <div className="mt-0.5 text-xs uppercase tracking-wide text-gray-500">{label}</div>
      {sub && <div className="mt-1 text-xs text-gray-400">{sub}</div>}
    </div>
  );
}

export function BehaviorPage() {
  const seg = useQuery({ queryKey: ['segments'], queryFn: getSegments, staleTime: 5 * 60 * 1000 });
  const met = useQuery({ queryKey: ['conversion-metrics'], queryFn: () => getConversionMetrics('90d'), staleTime: 5 * 60 * 1000 });

  const counts = seg.data?.counts ?? {};
  const order = seg.data?.order ?? Object.keys(SEGMENTS);
  const maxCount = Math.max(1, ...Object.values(counts));
  const chats = seg.data?.chats ?? [];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Behavior</h1>
        <p className="mt-1 text-sm text-gray-500">
          Per-group behavioral segment + the conversion funnel, from the nightly classifier. Click a group to open its detail.
        </p>
      </div>

      {/* Metrics (last 90d) */}
      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Funnel — last 90 days</h2>
        {met.isLoading ? (
          <div className="text-sm text-gray-400">Loading…</div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <MetricCard label="Installs" value={met.data?.installs ?? 0} />
            <MetricCard label="Reports sent" value={met.data?.reportsSent ?? 0} />
            <MetricCard label="Upgrades" value={met.data?.upgrades ?? 0} sub="LICENSE_CHANGED" />
            <MetricCard label="Activated" value={`${met.data?.activatedApproxPct ?? 0}%`} sub="got ≥1 report" />
          </div>
        )}
      </section>

      {/* Segment distribution */}
      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Segments {seg.data ? `· ${seg.data.total} groups` : ''}
        </h2>
        {seg.isLoading ? (
          <div className="text-sm text-gray-400">Loading…</div>
        ) : seg.data?.total === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500">
            No segments computed yet — the nightly classifier runs at 08:30 UTC. It can be triggered manually by invoking the
            <code className="mx-1 rounded bg-gray-200 px-1">Segment-Classification</code> Lambda.
          </div>
        ) : (
          <div className="space-y-1.5">
            {order.filter((s) => counts[s] > 0).map((s) => (
              <div key={s} className="flex items-center gap-3">
                <div className="w-40 shrink-0"><Pill segment={s} /></div>
                <div className="h-5 flex-1 overflow-hidden rounded bg-gray-100">
                  <div className="h-full rounded bg-sky-500/80" style={{ width: `${(counts[s] / maxCount) * 100}%` }} />
                </div>
                <div className="w-10 text-right text-sm tabular-nums text-gray-600">{counts[s]}</div>
                <div className="w-28 text-xs text-gray-400">{SEGMENTS[s]?.lever}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Per-group table */}
      {chats.length > 0 && (
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Groups by segment</h2>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Segment</th>
                  <th className="px-4 py-2 font-medium">Chat</th>
                  <th className="px-4 py-2 font-medium">Why</th>
                  <th className="px-4 py-2 font-medium">Plan</th>
                  <th className="px-4 py-2 text-right font-medium">Reports</th>
                  <th className="px-4 py-2 text-right font-medium">Posts/mo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {chats.map((c) => (
                  <tr key={c.chatId} className="hover:bg-gray-50">
                    <td className="px-4 py-2"><Pill segment={c.segment} /></td>
                    <td className="px-4 py-2">
                      <Link className="font-mono text-xs text-sky-700 hover:underline" to={`/chats/${encodeURIComponent(c.chatId)}`}>{c.chatId}</Link>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600">{c.reason}</td>
                    <td className="px-4 py-2 text-xs text-gray-600">{c.license ?? '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-700">{c.reportCount ?? '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-700">{c.monthlyPosts ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
